import { Box, Paper, Typography } from "@mui/material";
import type { UtilityConsumptionSummary } from "../../report/models/UtilityConsumption";
import {
	formatVolume,
	NO_DATA_TEXT,
	VOLUME_UNIT,
} from "../../report/utils/format";
import { CONSUMPTION_UNIT_COLOR } from "../../report/utils/tokens";

const formatChange = (percent: number) =>
	`${percent < 0 ? "↓" : "↑"} ${Math.abs(percent).toLocaleString("en-US", {
		maximumFractionDigits: 1,
	})}%`;

interface EngineeringHighlightsProps {
	summary: UtilityConsumptionSummary;
}

export const EngineeringHighlights = ({
	summary,
}: EngineeringHighlightsProps) => {
	const cells: {
		label: string;
		value: number | null;
		unit: string;
		changePercent?: number | null;
	}[] = [
		{
			label: "Last 7 days",
			value: summary.Last7Gallons,
			unit: VOLUME_UNIT,
			changePercent: summary.SevenDayChangePercent,
		},
		{
			label: "30-day avg",
			value: summary.ThirtyDayAverageGallons,
			unit: `${VOLUME_UNIT}/day`,
		},
		{
			label: `${summary.CurrentMonthLabel} to date`,
			value: summary.MonthToDateGallons,
			unit: VOLUME_UNIT,
		},
		{
			label: summary.PreviousMonthLabel,
			value: summary.LastMonthTotalGallons,
			unit: VOLUME_UNIT,
		},
		{
			label: "Year to date",
			value: summary.YearToDateGallons,
			unit: VOLUME_UNIT,
		},
	];

	return (
		<Paper
			component="section"
			variant="outlined"
			aria-label="Consumption at a glance"
			sx={{
				display: "grid",
				gap: 2,
				gridTemplateColumns: {
					xs: "repeat(2, minmax(0, 1fr))",
					md: "repeat(5, minmax(0, 1fr))",
				},
				p: 1.25,
			}}
		>
			{cells.map((cell) => (
				<Box key={cell.label} sx={{ display: "grid", gap: 0.25, minWidth: 0 }}>
					<Typography
						component="div"
						sx={{
							color: "#48525C",
							fontSize: "0.75rem",
							fontWeight: 500,
							lineHeight: 1.35,
						}}
					>
						{cell.label}
					</Typography>
					<Box
						sx={{
							alignItems: "baseline",
							display: "flex",
							flexWrap: "wrap",
							gap: 0.5,
							minWidth: 0,
						}}
					>
						<Typography
							component="span"
							sx={{
								color:
									cell.value === null ? CONSUMPTION_UNIT_COLOR : "text.primary",
								fontSize: "1.5rem",
								fontVariantNumeric: "tabular-nums",
								fontWeight: 600,
								lineHeight: 1.1,
							}}
						>
							{formatVolume(cell.value) ?? NO_DATA_TEXT}
						</Typography>
						{cell.value !== null && (
							<Typography
								component="span"
								sx={{
									color: CONSUMPTION_UNIT_COLOR,
									fontSize: "0.8rem",
									lineHeight: 1.2,
								}}
							>
								{cell.unit}
							</Typography>
						)}
						{cell.changePercent !== null &&
							cell.changePercent !== undefined && (
								<Typography
									component="span"
									sx={{
										color: (theme) =>
											Math.abs(cell.changePercent ?? 0) >= 5
												? (cell.changePercent ?? 0) < 0
													? theme.palette.success.main
													: theme.palette.error.main
												: undefined,
										fontSize: "0.8rem",
										fontWeight: 600,
										lineHeight: 1.2,
									}}
								>
									{formatChange(cell.changePercent)}
								</Typography>
							)}
					</Box>
				</Box>
			))}
		</Paper>
	);
};
